/* eslint-disable @typescript-eslint/no-misused-promises */
import { useForm } from "react-hook-form";
import {
  milestoneCompletionFormSchema,
  MilestoneCompletionFormSchema,
} from "./schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { BaseBillingTermMilestone, BillingPlanTerm } from "@/types";
import { useMarkMilestoneAsDelivered } from "./mutation";

const CreateMilestoneForm = ({
  jobOpeningId,
  milestoneOptions,
  onCancel,
  onSuccess,
}: {
  jobOpeningId: string;
  milestoneOptions: (BillingPlanTerm & BaseBillingTermMilestone)[];
  onCancel: () => void;
  onSuccess?: (data: BillingPlanTerm) => void;
}) => {
  const {
    markMilestoneAsComplete,
    isMarkingMilestoneAsComplete,
    markMilestoneAsCompleteError,
  } = useMarkMilestoneAsDelivered({
    jobOpeningId,
    onSuccess,
  });

  //   const offer = candidate.offers.at(-1);

  const form = useForm<MilestoneCompletionFormSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(milestoneCompletionFormSchema)(data, context, opts);
    },
  });

  function onSubmit(data: MilestoneCompletionFormSchema) {
    if (isMarkingMilestoneAsComplete) {
      return;
    }

    const payload = {
      ...data,
      completionDate: new Date(data.completionDate),
    };

    payload.completionDate.setHours(12);

    markMilestoneAsComplete(payload);
  }

  return (
    <Form {...form}>
      {markMilestoneAsCompleteError && (
        <p className="text-center w-full border border-destructive rounded-md text-destructive p-2 bg-destructive/10">
          Something went wrong when making a billing plan, please try again
          later.
        </p>
      )}
      <form className="space-y-4 w-full" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="milestone"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Milestone</FormLabel>
              <FormControl>
                <Select
                  name={field.name}
                  value={field.value}
                  onValueChange={field.onChange}
                  disabled={field.disabled}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a milestone" />
                  </SelectTrigger>
                  <SelectContent>
                    {milestoneOptions.map((milestone) => (
                      <SelectItem
                        key={`milestone-${milestone.id}`}
                        value={milestone.id}
                      >
                        {milestone.milestoneLabel}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="completionDate"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Date completed</FormLabel>
              <FormControl>
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  modal
                  allowBackdating
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="h-[1px] bg-[#E4E7EC] w-full" />
        <div className="!mb-1 flex justify-between">
          <Button variant="stroke" className="mt-2" onClick={onCancel}>
            Cancel
          </Button>

          <Button
            variant="success"
            type="submit"
            className="mt-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={form.handleSubmit(onSubmit)}
            disabled={isMarkingMilestoneAsComplete}
          >
            Confirm
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateMilestoneForm;
